import { WorkflowEventType } from "generated/graphql";
import { entityTypeNoun } from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

export interface ConditionSegment {
    text: string;
    statusIds: string[];
}

export interface TriggerCondition {
    key: string;
    segments: ConditionSegment[];
}

export type UserNameResolver = (userId: string) => string;

interface TriggerValue {
    values: string[] | null;
    specialValue: string | null;
}

interface TriggerMatcher {
    fieldName: string;
    current: TriggerValue | null;
    previous: TriggerValue | null;
}

const NOT_EMPTY_DESCRIPTIONS: Record<string, (noun: string) => string> = {
    chosenCommunityId: noun => `A chosen community is selected for the ${noun}`,
    referralContactId: noun => `A referral source is set for the ${noun}`,
    moveTimelineActual: noun => `A move date is set for the ${noun}`,
    statusCheckLaterDate: noun => `A check back later date is set for the ${noun}`,
    actualStartDate: noun => `A care start date is set for the ${noun}`,
    businessId: noun => `The ${noun} is attached to an organization`,
};

const PREVIOUSLY_EMPTY_DESCRIPTIONS: Record<string, (noun: string) => string> = {
    businessId: noun => `The ${noun} was not attached to an organization before`,
};

const isStatusField = (fieldName: string): boolean =>
    fieldName === "status" || fieldName === "statusListItem";

const sentence = (text: string): ConditionSegment[] => [{ text, statusIds: [] }];

const entityUpdatedMatchers = (workflow: EditableWorkflow): TriggerMatcher[] =>
    (workflow.trigger?.entityUpdated?.fieldMatchers ?? []).map(
        (matcher): TriggerMatcher => ({
            fieldName: matcher.fieldName,
            current: matcher.current ?? null,
            previous: matcher.previous ?? null,
        }),
    );

const noteAddedMatchers = (workflow: EditableWorkflow): TriggerMatcher[] =>
    (workflow.trigger?.noteAdded?.entityFieldMatchers ?? []).map(
        (matcher): TriggerMatcher => ({
            fieldName: matcher.fieldName,
            current: matcher.current ?? null,
            previous: null,
        }),
    );

const triggerMatchers = (workflow: EditableWorkflow): TriggerMatcher[] => {
    switch (workflow.trigger?.eventType) {
        case WorkflowEventType.EntityUpdated:
            return entityUpdatedMatchers(workflow);
        case WorkflowEventType.NoteAdded:
            return noteAddedMatchers(workflow);
        default:
            return [];
    }
};

const describeStatusMatcher = (
    matcher: TriggerMatcher,
    noun: string,
): ConditionSegment[] => {
    const current = matcher.current?.values ?? [];
    const previous = matcher.previous?.values ?? [];
    if (previous.length > 0) {
        return [
            { text: `The ${noun}'s status changes from`, statusIds: previous },
            { text: "to", statusIds: current },
        ];
    }
    if (matcher.current?.specialValue === "$CHANGED") {
        return sentence(`The ${noun}'s status changes`);
    }
    if (current.length === 0) {
        return [];
    }
    return [{ text: `The ${noun}'s status is one of:`, statusIds: current }];
};

const describeAssignedUsers = (
    matcher: TriggerMatcher,
    noun: string,
    userName: UserNameResolver,
): ConditionSegment[] => {
    const names = (matcher.current?.values ?? []).map(userName).filter(Boolean);
    if (names.length === 0) {
        return sentence(`The ${noun} is assigned to specific users`);
    }
    return sentence(`The ${noun} is assigned to ${names.join(", ")}`);
};

const describeMatcher = (
    matcher: TriggerMatcher,
    noun: string,
    userName: UserNameResolver,
): ConditionSegment[] => {
    if (isStatusField(matcher.fieldName)) {
        return describeStatusMatcher(matcher, noun);
    }
    if (matcher.fieldName === "assignedUserId") {
        return describeAssignedUsers(matcher, noun, userName);
    }
    const notEmpty = NOT_EMPTY_DESCRIPTIONS[matcher.fieldName];
    if (matcher.current?.specialValue === "$NOT_EMPTY" && notEmpty) {
        return sentence(notEmpty(noun));
    }
    const previouslyEmpty = PREVIOUSLY_EMPTY_DESCRIPTIONS[matcher.fieldName];
    if (
        !matcher.current &&
        matcher.previous?.specialValue === "$EMPTY" &&
        previouslyEmpty
    ) {
        return sentence(previouslyEmpty(noun));
    }
    return sentence(`An advanced condition applies to ${matcher.fieldName}`);
};

export const triggerConditions = (
    workflow: EditableWorkflow,
    userName: UserNameResolver,
): TriggerCondition[] => {
    const noun = entityTypeNoun(workflow.attachTo);
    return triggerMatchers(workflow)
        .map((matcher, index) => ({
            key: `${matcher.fieldName}-${index}`,
            segments: describeMatcher(matcher, noun, userName),
        }))
        .filter(condition => condition.segments.length > 0);
};

export const assignedUserNames = (
    workflow: EditableWorkflow,
    userName: UserNameResolver,
): string[] =>
    triggerMatchers(workflow)
        .filter(matcher => matcher.fieldName === "assignedUserId")
        .flatMap(matcher => matcher.current?.values ?? [])
        .map(userName)
        .filter(Boolean);

export const hasAssignedUserGate = (workflow: EditableWorkflow): boolean =>
    triggerMatchers(workflow).some(matcher => matcher.fieldName === "assignedUserId");

export const hasStatusChangeFromGate = (workflow: EditableWorkflow): boolean =>
    triggerMatchers(workflow).some(
        matcher =>
            isStatusField(matcher.fieldName) &&
            (matcher.previous?.values ?? []).length > 0,
    );

export const hasNoteAddedTrigger = (workflow: EditableWorkflow): boolean =>
    workflow.trigger?.eventType === WorkflowEventType.NoteAdded;
